import { Checkbox, CheckboxProps } from '@chakra-ui/react';
import React from 'react';
import { ButtonListField, ButtonListFieldProps } from './ButtonList';
import { CheckAndCountField, CheckAndCountFieldProps } from './CheckAndCountField';
import { CheckboxGroupField, CheckboxGroupFieldProps } from './CheckboxGroupField';
import {
  CheckboxWithDetailsField,
  CheckboxWithDetailsFieldProps,
} from './CheckboxWithDetailsField';
import { DateRangePickField, DateRangePickFieldProps } from './DateRangePick';
import {
  DateRangePickFieldSeparated,
  DateRangePickFieldSeparatedProps,
} from './DateRangePickSeparated';
import { FreeFormSelectField, FreeFormSelectFieldProps } from './FreeFormSelect';
import { InputField, InputFieldProps } from './InputField';
import { MultiSelectField, MultiSelectFieldProps } from './MultiSelectField';
import { RadioButtonField, RadioButtonFieldProps } from './RadioField';
import { SelectField, SelectFieldProps } from './SelectField';
import { SelectableOptionsField, SelectableOptionsFieldProps } from './SelectableOptionsField';
import { SingleDatePickField, SingleDatePickFieldProps } from './SingleDatePick';
import { TextAreaField, TextAreaFieldProps } from './TextAreaField';
import { useField } from './useField';
import { useValidate } from './useValidate';
import type { Validation, Validator } from './validation';
import {
  TextAreaWithCheckboxField,
  TextAreaWithCheckboxFieldProps,
} from './TextAreaWithCheckboxField';
import { DateTimePickField, DateTimePickFieldProps } from './DateTimePickField';

type InputFieldType = 'text' | 'email' | 'number' | 'password' | 'tel' | 'url' | 'date';
export type FieldType =
  | 'checkbox'
  | 'radio'
  | 'buttonList'
  | 'textArea'
  | 'textAreaWithCheckbox'
  | 'select'
  | 'freeFormSelect'
  | 'dateRangePick'
  | 'dateRangePickSeparated'
  | 'dateTimePick'
  | 'singleDatePick'
  | 'multiSelect'
  | 'checkAndCount'
  | 'checkboxGroup'
  | 'selectableOptions'
  | 'checkboxWithDetails'
  | InputFieldType;

export interface FieldOptions {
  /**
   * The option label.
   */
  label: string;
  /**
   * The option value.
   */
  value: string;
}

export interface FieldTypeProps {
  checkbox: CheckboxProps;
  radio: RadioButtonFieldProps;
  text: InputFieldProps;
  textArea: TextAreaFieldProps;
  textAreaWithCheckbox: TextAreaWithCheckboxFieldProps;
  email: InputFieldProps;
  number: InputFieldProps;
  password: InputFieldProps;
  tel: InputFieldProps;
  url: InputFieldProps;
  date: InputFieldProps;
  buttonList: ButtonListFieldProps;
  select: SelectFieldProps;
  freeFormSelect: FreeFormSelectFieldProps;
  dateRangePick: DateRangePickFieldProps;
  dateRangePickSeparated: DateRangePickFieldSeparatedProps;
  dateTimePick: DateTimePickFieldProps;
  singleDatePick: SingleDatePickFieldProps;
  multiSelect: MultiSelectFieldProps;
  checkAndCount: CheckAndCountFieldProps;
  checkboxGroup: CheckboxGroupFieldProps;
  selectableOptions: SelectableOptionsFieldProps;
  checkboxWithDetails: CheckboxWithDetailsFieldProps;
}

export interface FieldProps<T extends FieldType> {
  label?: React.ReactNode;
  name: string;
  type: T;
  validate?: Validation | Validator;
  validationError?: string;
  isDisabled?: boolean;
}

export type FieldComponentProps<T extends FieldType> = FieldProps<T> & FieldTypeProps[T];
type FieldElement<T extends FieldType> = React.ReactElement<FieldComponentProps<T>>;

export const Field = <T extends FieldType>({
  label,
  name,
  type,
  validate: validateProp,
  validationError,
  isDisabled,
  ...props
}: FieldComponentProps<T>): FieldElement<T> => {
  const validate = useValidate(validateProp, validationError);
  const [field, meta, helpers] = useField({
    name,
    type,
    validate,
  });

  const error = (meta.touched && meta.error) || undefined;

  switch (type) {
    case 'checkbox':
      return (
        <Checkbox
          {...(props as FieldTypeProps['checkbox'])}
          checked={field.checked}
          error={error}
          helpers={helpers}
          label={label}
          name={name}
          isDisabled={isDisabled}
        />
      );
    case 'buttonList':
      return (
        <ButtonListField
          {...(props as unknown as FieldTypeProps['buttonList'])}
          isDisabled={isDisabled}
        />
      );
    case 'textArea':
      return (
        <TextAreaField
          {...(props as unknown as FieldTypeProps['textArea'])}
          error={error}
          errors={error}
          helpers={helpers}
          label={label}
          name={name}
          type={type}
          value={field.value}
          isDisabled={isDisabled}
        />
      );
    case 'select':
      return (
        <SelectField
          {...(props as unknown as FieldTypeProps['select'])}
          error={error}
          helpers={helpers}
          label={label}
          name={name}
          value={field.value}
          field={field}
          isDisabled={isDisabled}
        />
      );
    case 'freeFormSelect':
      return (
        <FreeFormSelectField
          {...(props as unknown as FieldTypeProps['freeFormSelect'])}
          error={error}
          helpers={helpers}
          label={label}
          name={name}
          value={field.value}
          field={field}
          meta={meta}
          isDisabled={isDisabled}
        />
      );
    case 'dateRangePick':
      return (
        <DateRangePickField
          {...(props as unknown as FieldTypeProps['dateRangePick'])}
          errors={error}
          helpers={helpers}
          label={label}
          name={name}
          value={field.value}
          setFieldValue={helpers.setValue}
          setTouched={helpers.setTouched}
          isDisabled={isDisabled}
        />
      );
    case 'dateRangePickSeparated':
      return (
        <DateRangePickFieldSeparated
          {...(props as unknown as FieldTypeProps['dateRangePickSeparated'])}
          errors={error}
          helpers={helpers}
          label={label}
          name={name}
          value={field.value}
          setFieldValue={helpers.setValue}
          setTouched={helpers.setTouched}
          isDisabled={isDisabled}
        />
      );
    case 'dateTimePick':
      return (
        <DateTimePickField
          {...(props as unknown as FieldTypeProps['dateTimePick'])}
          errors={error}
          helpers={helpers}
          label={label}
          name={name}
          value={field.value}
          setFieldValue={helpers.setValue}
          setTouched={helpers.setTouched}
          isDisabled={isDisabled}
        />
      );
    case 'singleDatePick':
      return (
        <SingleDatePickField
          {...(props as unknown as FieldTypeProps['singleDatePick'])}
          errors={error}
          helpers={helpers}
          label={label}
          name={name}
          value={field.value}
          setFieldValue={helpers.setValue}
          setTouched={helpers.setTouched}
          isDisabled={isDisabled}
        />
      );
    case 'radio':
      return (
        <RadioButtonField
          {...(props as unknown as FieldTypeProps['radio'])}
          error={error}
          label={label}
          name={name}
          onChange={(value: string) => helpers.setValue(value)}
          isDisabled={isDisabled}
        />
      );
    case 'multiSelect':
      return (
        <MultiSelectField
          {...(props as unknown as FieldTypeProps['multiSelect'])}
          label={label}
          value={field.value}
          helpers={helpers}
          setFieldValue={helpers.setValue}
          name={name}
          isDisabled={isDisabled}
        />
      );
    case 'checkAndCount':
      return (
        <CheckAndCountField
          {...(props as unknown as FieldTypeProps['checkAndCount'])}
          error={error}
          helpers={helpers}
          field={field}
          meta={meta}
          label={label}
          name={name}
          isDisabled={isDisabled}
        />
      );
    case 'checkboxGroup':
      return (
        <CheckboxGroupField
          {...(props as unknown as FieldTypeProps['checkboxGroup'])}
          error={error}
          helpers={helpers}
          field={field}
          meta={meta}
          label={label}
          name={name}
          value={field.value}
          isDisabled={isDisabled}
        />
      );
    case 'selectableOptions':
      return (
        <SelectableOptionsField
          {...(props as unknown as FieldTypeProps['selectableOptions'])}
          error={error}
          helpers={helpers}
          field={field}
          meta={meta}
          label={label}
          name={name}
          isDisabled={isDisabled}
        />
      );
    case 'checkboxWithDetails':
      return (
        <CheckboxWithDetailsField
          {...(props as unknown as FieldTypeProps['checkboxWithDetails'])}
          error={error}
          helpers={helpers}
          field={field}
          meta={meta}
          label={label}
          name={name}
          isDisabled={isDisabled}
        />
      );
    case 'textAreaWithCheckbox':
      return (
        <TextAreaWithCheckboxField
          {...(props as unknown as FieldTypeProps['textAreaWithCheckbox'])}
          error={error}
          errors={error}
          helpers={helpers}
          label={label}
          name={name}
          value={field.value}
          isDisabled={isDisabled}
        />
      );
    default:
      return (
        <InputField
          {...(props as unknown as FieldTypeProps['text'])}
          error={error}
          errors={error}
          helpers={helpers}
          label={label}
          name={name}
          type={type}
          value={field.value}
          field={field}
          isDisabled={isDisabled}
        />
      );
  }
};
